import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Table } from "antd";
import {
  SearchOutlined,
  ArrowLeftOutlined,
  GroupOutlined,
  MessageOutlined, // ✅ icon for View Chat
} from "@mui/icons-material";
import { useGetAllUsers } from "../../../api/addEmployee/employee";

const EmployeeChat = () => {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const navigate = useNavigate();
  const { data: users = [], isLoading } = useGetAllUsers();

  // Extract Unique Departments
  const departments = useMemo(() => {
    if (!users || users.length === 0) return [];
    const departmentMap = {};
    users.forEach((user) => {
      const deptName = user.department || "No Department";
      if (!departmentMap[deptName]) {
        departmentMap[deptName] = { name: deptName, count: 0 };
      }
      departmentMap[deptName].count += 1;
    });
    return Object.values(departmentMap);
  }, [users]);

  // Filter Users by Selected Department
  const filteredUsers = useMemo(() => {
    let deptUsers = selectedDepartment
      ? users.filter(
          (user) => (user.department || "No Department") === selectedDepartment
        )
      : users;

    if (!searchText) return deptUsers;

    return deptUsers.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.mobile?.includes(searchText)
    );
  }, [searchText, users, selectedDepartment]);

  // Employee Table Columns
  const employeeColumns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1 + (page - 1) * pageSize,
    },
    {
      title: "Name",
      render: (_, record) => (
        <span className="text-textBlack">
          {record.firstName} {record.lastName}
        </span>
      ),
    },
    { title: "Mobile", dataIndex: "mobile", responsive: ["md"] },
    { title: "E-Mail", dataIndex: "email", responsive: ["lg"] },
    { title: "Password", dataIndex: "plainPassword", responsive: ["xl"] },
    {
      title: "Action",
      width: 150,
      render: (_, row) => (
        <Button
          icon={<MessageOutlined />}
          className="!bg-primary !border-primary text-white"
          onClick={() => navigate(`/chat?userId=${row._id}`)} // ✅ pass userId to ChatLayout
        >
          View Chat
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-4 max-h-screen overflow-auto">
      {/* Back Button */}
      {selectedDepartment && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => setSelectedDepartment(null)}
          className="mb-4"
        >
          Back to Departments
        </Button>
      )}

      {/* Search Bar */}
      {selectedDepartment && (
        <div className="flex justify-between flex-wrap gap-4 items-center">
          <div className="relative w-80">
            <Input
              placeholder="Search employee..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </div>
        </div>
      )}

      {/* Department Cards or Employee Table */}
      {!selectedDepartment ? (
        <div className="space-y-4">
          {/* Department Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {departments.map((dept, index) => (
              <div
                key={index}
                className="bg-white border rounded-2xl shadow hover:shadow-lg p-4 flex flex-col justify-between transition-all"
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <GroupOutlined className="text-primary" />
                    <h2 className="text-lg font-semibold">{dept.name}</h2>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Total Employees:{" "}
                    <span className="font-semibold">{dept.count}</span>
                  </p>
                </div>
                <Button
                  type="primary"
                  className="mt-4 w-full"
                  onClick={() => setSelectedDepartment(dept.name)}
                >
                  View Employees
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Table
          rowKey="_id"
          columns={employeeColumns}
          dataSource={filteredUsers}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: filteredUsers.length,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 40],
            onChange: (current, size) => {
              setPage(current);
              setPageSize(size);
            },
          }}
        />
      )}
    </div>
  );
};

export default EmployeeChat;
